import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useClaimStore } from '../store/claimStore';
import { useNetworkStatus } from './useNetworkStatus';
import { logger } from '../utils/logger';

/**
 * Custom hook for automatic draft saving
 *
 * Watches stepData changes and automatically saves the claim draft after a debounce period.
 * This ensures user data is persisted even if they don't click "Next" button.
 *
 * Features:
 * - Debounced saving (default 2s after last change)
 * - Deep comparison to detect actual changes (skips if data unchanged)
 * - Only saves if claim exists (eventId present or stepData has content)
 * - Skips saving if already loading
 * - Automatically retries save when network comes back online
 * - Cleans up timers on unmount
 *
 * @param options Configuration options
 * @param options.enabled Enable/disable auto-save (default: true)
 * @param options.debounceMs Debounce delay in milliseconds (default: 2000ms)
 * @param options.excludePaths Paths where auto-save should be disabled (default: ['/', '/claim/05-03-FIN'])
 *
 * @example
 * // In App.tsx for global auto-save
 * function App() {
 *   useAutoSave({ enabled: true, debounceMs: 2000 });
 *   return <Routes>...</Routes>;
 * }
 *
 * @example
 * // In specific screen with custom debounce
 * function MedicalDetailsScreen() {
 *   useAutoSave({ enabled: true, debounceMs: 5000 });
 *   return <form>...</form>;
 * }
 */
export const useAutoSave = (options: {
  enabled?: boolean;
  debounceMs?: number;
  excludePaths?: string[];
} = {}) => {
  const { enabled = true, debounceMs = 2000, excludePaths = ['/', '/claim/05-03-FIN'] } = options;

  const location = useLocation();
  const { isOnline, wasOffline } = useNetworkStatus();

  const saveDraft = useClaimStore(state => state.saveDraft);
  const stepData = useClaimStore(state => state.stepData);
  const eventId = useClaimStore(state => state.eventId);
  const isLoading = useClaimStore(state => state.isLoading);
  const currentClaim = useClaimStore(state => state.currentClaim);
  const referenceClaim = useClaimStore(state => state.referenceClaim);
  const deferClaimCreation = useClaimStore(state => state.deferClaimCreation);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);
  const previousStepDataRef = useRef<typeof stepData | null>(null);
  const previousEventIdRef = useRef<string | null | undefined>(eventId);
  const hasPendingSave = useRef<boolean>(false);

  useEffect(() => {
    // When the claim changes (different eventId), reset the baseline without saving.
    // This prevents a spurious auto-save when loadDraft restores stepData for a different claim.
    if (previousEventIdRef.current !== eventId) {
      previousEventIdRef.current = eventId;
      previousStepDataRef.current = stepData;
      isFirstRender.current = false;
      return;
    }

    // Skip on first render (don't save immediately on mount)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousStepDataRef.current = stepData;
      return;
    }

    // Skip if stepData hasn't actually changed (deep comparison)
    if (previousStepDataRef.current &&
        JSON.stringify(stepData) === JSON.stringify(previousStepDataRef.current)) {
      return;
    }

    // Skip if disabled
    if (!enabled) {
      return;
    }

    // Skip if on excluded path (e.g., confirmation screen)
    if (excludePaths.some(path => location.pathname === path)) {
      return;
    }

    // Skip if no claim initialized yet
    if (!currentClaim) {
      return;
    }

    // Skip if a reference claim is loaded — indicates a flow that acts on an already-submitted
    // claim in read-only mode (e.g., DEV-6623 "append expenses"). These flows explicitly must
    // NOT persist a draft Submission; the final write goes through a dedicated endpoint
    // (POST /api/v1/claims/{eventId}/append) at the end of the flow.
    if (referenceClaim) {
      return;
    }

    // ADR-061 (Deferred Claim Creation) — PRIMARY guard. While creation is deferred and no claim
    // has been created yet (eventId == null), never arm the debounce timer: emitting a draft POST
    // before the contract is selected would create the Event__c with an empty contractNumber and
    // be rejected by the BFF ("Contract number is required to create a claim"). The flag is lowered
    // at 01-01-CONTRAT once the contract is chosen. The `!eventId` term preserves the
    // existing-event flow (createNewClaimOnExistingEvent sets eventId, so this guard is inert there).
    if (deferClaimCreation && !eventId) {
      return;
    }

    // Skip if already saving
    if (isLoading) {
      return;
    }

    // Skip if stepData is empty (no data to save)
    if (!stepData || Object.keys(stepData).length === 0) {
      return;
    }

    // Clear previous timer if exists
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set new debounced timer
    timerRef.current = setTimeout(() => {
      // Update reference to current stepData before saving
      previousStepDataRef.current = stepData;

      saveDraft()
        .then(() => {
          hasPendingSave.current = false;
        })
        .catch((error) => {
          logger.error('Auto-save failed to save draft', {
            errorType: 'AutoSaveError',
            hook: 'useAutoSave'
          }, error as Error);
          // Mark that we have unsaved changes
          hasPendingSave.current = true;
        });
    }, debounceMs);

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [stepData, eventId, enabled, debounceMs, isLoading, currentClaim, deferClaimCreation, saveDraft, location.pathname, excludePaths]);

  // Watch for network coming back online and retry save if there were pending changes
  useEffect(() => {
    // Only trigger if we just came back online AND have pending unsaved changes
    if (isOnline && wasOffline && hasPendingSave.current) {
      // Don't retry if disabled, no claim, already loading, or on excluded path
      if (!enabled || !currentClaim || isLoading || excludePaths.some(path => location.pathname === path)) {
        return;
      }

      // ADR-061 (Deferred Claim Creation) — same guard as the main effect: never emit a draft POST
      // before the contract is selected, even on a network-restoration retry.
      if (deferClaimCreation && !eventId) {
        return;
      }

      // Retry the save
      saveDraft()
        .then(() => {
          hasPendingSave.current = false;
        })
        .catch((error) => {
          logger.error('Auto-save failed after network restoration', {
            errorType: 'AutoSaveNetworkRetryError',
            hook: 'useAutoSave'
          }, error as Error);
        });
    }
  }, [isOnline, wasOffline, enabled, currentClaim, isLoading, deferClaimCreation, eventId, saveDraft, location.pathname, excludePaths]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
};
